// Root shell. Plan 23 reduces this further:
//   - services / bookings / purchases / cart state all migrated into
//     React Query hooks + the CartProvider (in src/features/booking/CartProvider.tsx).
//   - The only local state remaining is the BookingModal / selectedPurchase
//     trio, because those are strictly presentational toggles that don't
//     belong in a hook.
//
// Plan 20 established the feature-based tree; plan 21 wraps each route in
// React.lazy + Suspense so every route ships as its own chunk. Plan 23
// keeps those lazy boundaries intact — adding new state to this file
// should be a code-review red flag.

import { lazy, Suspense, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { Loader2 } from './shared/icons';
import type { Purchase, Service } from './shared/types';
import { RouterProvider, navigate, useRoute } from './app/router';
import { PageFallback } from './shared/ui/PageFallback';
import {
  AboutSection,
  AdminDashboardPage,
  ContactPage,
  ContributionPage,
  DashboardPage,
  GiftCertificatePage,
  LandingPage,
  LearnMore,
  NetworkPage,
  ServiceDetailPage,
  TermsAndConditions,
  TestimonialSubmissionPage,
} from './app/routes';
// Color-animation tuning tool — permanent, reachable at /#shimmer-lab.
const ShimmerLab = lazy(() =>
  import('./features/shimmer-lab/ShimmerLab').then((m) => ({ default: m.ShimmerLab }))
);
// Typography tuning tool — permanent, reachable at /#font-lab.
const FontLab = lazy(() =>
  import('./features/font-lab/FontLab').then((m) => ({ default: m.FontLab }))
);
import { useAuth } from './features/auth/AuthProvider';
import { AuthModal } from './features/auth/AuthModal';
import { ResetPasswordPage } from './features/auth/ResetPasswordPage';
import { Navbar } from './features/navbar/Navbar';
import { Footer } from './features/footer/Footer';
import { CartTray } from './features/cart/CartTray';
import { useCart } from './features/booking/CartProvider';

const BookingModal = lazy(() =>
  import('./features/booking/BookingModal').then((m) => ({ default: m.BookingModal }))
);
const StagingHelper = lazy(() =>
  import('./features/staging-helper/StagingHelper').then((m) => ({ default: m.StagingHelper }))
);

function AppShell() {
  const { user, isAuthenticated, loading, openAuthModal } = useAuth();
  const { clear: clearCart } = useCart();
  const { route, serviceSlug } = useRoute();

  // Modal booking flow (service selection + purchase completion). These
  // are strictly presentational and local to the shell — they don't need
  // a hook or context.
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [selectedService] = useState<Service | null>(null);
  const [selectedPurchase, setSelectedPurchase] = useState<Purchase | null>(null);

  // `?login=1` deep link (2026-07-11) — the JS-free Astro headers
  // (blog/catalog) can't open the auth modal, so their "Client Access"
  // links here. Consume the param once auth state is known: guests get
  // the auth modal, signed-in users go straight to their dashboard. The
  // param is stripped so a refresh doesn't re-trigger it.
  useEffect(() => {
    if (loading) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('login') !== '1') return;
    params.delete('login');
    const query = params.toString();
    history.replaceState(
      '',
      document.title,
      `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`
    );
    if (isAuthenticated) {
      navigate(user?.role === 'admin' ? 'admin' : 'dashboard');
    } else {
      openAuthModal();
    }
    // Run once when the auth probe settles — not on later auth changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="text-center space-y-4">
          <Loader2 className="w-12 h-12 text-stone-900 animate-spin mx-auto" />
          <p className="text-stone-400 font-serif lowercase italic">Attuning...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-stone-50 text-stone-900 font-sans selection:bg-stone-900 selection:text-stone-50">
      <Navbar />

      <Suspense fallback={<PageFallback />}>
        <AnimatePresence mode="wait">
          {route === 'landing' && <LandingPage />}
          {route === 'terms' && (
            <motion.div
              key="terms"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <TermsAndConditions />
            </motion.div>
          )}
          {route === 'about' && <AboutSection />}
          {route === 'contact' && <ContactPage />}
          {route === 'service-detail' && (
            <motion.div
              key={`service-${serviceSlug}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <ServiceDetailPage slug={serviceSlug} />
            </motion.div>
          )}
          {route === 'dashboard' && isAuthenticated && (
            <motion.div
              key="dashboard"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <DashboardPage
                onCompletePurchase={(p) => {
                  setSelectedPurchase(p);
                  setIsBookingModalOpen(true);
                }}
              />
            </motion.div>
          )}
          {route === 'testimonials' && (
            <motion.div
              key="testimonials"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <TestimonialSubmissionPage />
            </motion.div>
          )}
          {route === 'contribute' && (
            <motion.div
              key="contribute"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <ContributionPage />
            </motion.div>
          )}
          {route === 'gift' && (
            <motion.div
              key="gift"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <GiftCertificatePage />
            </motion.div>
          )}
          {route === 'network' && (
            <motion.div
              key="network"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <NetworkPage />
            </motion.div>
          )}
          {route === 'learn' && (
            <motion.div
              key="learn"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <LearnMore />
            </motion.div>
          )}
          {route === 'shimmer-lab' && (
            <motion.div
              key="shimmer-lab"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <ShimmerLab />
            </motion.div>
          )}
          {route === 'font-lab' && (
            <motion.div
              key="font-lab"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <FontLab />
            </motion.div>
          )}
          {route === 'admin' && isAuthenticated && user?.role === 'admin' && (
            <motion.div
              key="admin"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <AdminDashboardPage />
            </motion.div>
          )}
        </AnimatePresence>
      </Suspense>

      <Footer />

      <AuthModal />

      <CartTray
        onCheckout={() => {
          if (!isAuthenticated) {
            openAuthModal({ pendingAction: () => setIsBookingModalOpen(true) });
          } else {
            setIsBookingModalOpen(true);
          }
        }}
      />

      {isBookingModalOpen && (
        <Suspense fallback={null}>
          <BookingModal
            isOpen={isBookingModalOpen}
            onClose={() => {
              setIsBookingModalOpen(false);
              setSelectedPurchase(null);
            }}
            service={selectedService}
            onSuccess={(opts) => {
              clearCart();
              // BookingModal hints which dashboard tab the buyer should
              // land on — 'sessions' (Upcoming) for SESSION purchases,
              // 'history' (Vault) for CONTENT deliverables. We pass the
              // hint via a ?dashboardTab=... search param that
              // DashboardPage consumes on mount and strips so a refresh
              // doesn't re-trigger the redirect.
              if (opts?.dashboardTab) {
                const params = new URLSearchParams(window.location.search);
                params.set('dashboardTab', opts.dashboardTab);
                history.replaceState(
                  '',
                  '',
                  `${window.location.pathname}?${params.toString()}#dashboard`
                );
                window.dispatchEvent(new HashChangeEvent('hashchange'));
              } else {
                navigate('dashboard');
              }
            }}
            initialPurchase={selectedPurchase}
          />
        </Suspense>
      )}
      {!import.meta.env.PROD && (
        <Suspense fallback={null}>
          <StagingHelper />
        </Suspense>
      )}
    </div>
  );
}

/**
 * Plan 75 — `/reset-password` is the email-link landing for the password
 * reset flow. It runs OUTSIDE the normal app shell (no Navbar / Footer /
 * AuthModal) because the user followed an external link from a
 * logged-out state and we want a single-purpose surface. The hash router
 * is unused on this path.
 */
function isPasswordResetPath(): boolean {
  return typeof window !== 'undefined' && window.location.pathname === '/reset-password';
}

export default function App() {
  if (isPasswordResetPath()) {
    return <ResetPasswordPage />;
  }
  return (
    <RouterProvider>
      <AppShell />
    </RouterProvider>
  );
}
